(function () {
  // Lightweight, client-side wishlist. Persistence is via localStorage —
  // there is no Shopify backend wishlist feature and no app installed for
  // this, so saved items live per-browser only (not synced across a
  // customer's devices, and cleared if the browser's storage is cleared).
  // That's a real limitation worth knowing about, not a hidden one.
  const STORAGE_KEY = 'blm-wishlist';

  function readWishlist() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      // Storage unavailable (private browsing, quota, etc.) — toggling
      // still updates the UI for this page view, it just won't persist.
    }
    document.dispatchEvent(new CustomEvent('blm:wishlist:change', { detail: { handles } }));
  }

  function isWishlisted(handle) {
    return !!handle && readWishlist().includes(handle);
  }

  function addToWishlist(handle) {
    if (!handle) return;
    const handles = readWishlist();
    if (!handles.includes(handle)) {
      handles.push(handle);
      writeWishlist(handles);
    }
  }

  function removeFromWishlist(handle) {
    if (!handle) return;
    writeWishlist(readWishlist().filter((item) => item !== handle));
  }

  function toggleWishlist(handle) {
    if (!handle) return false;
    const nowActive = !isWishlisted(handle);
    if (nowActive) addToWishlist(handle);
    else removeFromWishlist(handle);
    return nowActive;
  }

  function syncButton(button) {
    const handle = button.getAttribute('data-wishlist-toggle');
    const active = isWishlisted(handle);
    button.classList.toggle('is-wishlisted', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function syncAllButtons(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
  }

  // Mirrors cart-bubble's own markup/behavior exactly (same classes, same
  // hidden-when-zero / shown-when-nonzero pattern, same 99+ cap), just fed
  // from localStorage instead of the cart object.
  function syncBubbles() {
    const count = readWishlist().length;
    const display = count > 99 ? '99+' : count > 0 ? String(count) : '';

    document.querySelectorAll('[data-wishlist-bubble]').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', count === 0);

      const countEl = bubble.querySelector('[data-wishlist-bubble-count]');
      if (countEl) countEl.textContent = display;
    });
  }

  // Capture phase, and stop propagation: these buttons commonly sit inside
  // or beside a product card's own full-card link, and must never trigger
  // that link's navigation.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      toggleWishlist(button.getAttribute('data-wishlist-toggle'));
    },
    { capture: true }
  );

  document.addEventListener('blm:wishlist:change', () => {
    syncAllButtons();
    syncBubbles();
  });
  document.addEventListener('shopify:section:load', (event) => {
    syncAllButtons(event.target);
    syncBubbles();
  });

  // Collection pages append more product cards via infinite scroll after
  // initial load — resync newly-added heart buttons when that happens.
  let resyncTimeout;
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length) {
        clearTimeout(resyncTimeout);
        resyncTimeout = setTimeout(() => syncAllButtons(), 60);
        break;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(() => {
    syncAllButtons();
    syncBubbles();
  });

  window.BlmWishlist = {
    get: readWishlist,
    isWishlisted,
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist,
  };
})();
